import { Typography } from "@mui/material"
import { translate } from "app/language/service"
import { APP_CONTEXTS, NOT_SAVED_ID } from "app/model"
import { getActiveAppContext, setActiveAppContext } from "app/tools/common"
import { CampaignStatus } from "campaign/components/misc/CampaignStatus"
import { CAMPAIGN_STATUSES } from "campaign/model"
import { FormInventory } from "form/inventory/components/FormInventory"
import { IForm, IFormError } from "form/model"
import { getFormActionButtons } from "form/tools/action-buttons"
import { getForm, notifySummaryClosed, notifySummaryOpened } from "form/tools/common"
import { isCampaignLaunchableInFormContext, translatedLaunchError } from "form/tools/launch"
import { loadCampaign } from "form/tools/load"
import { closeCurrentSummary } from "form/tools/summary"
import { NotFoundPage } from "page/components/NotFoundPage"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { useParams } from "react-router-dom"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { PageHeader } from "ui/components/main-layout/PageHeader"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { canSaveCampaigns } from "user/tools/permissions"
import { SummaryCampaign } from "./SummaryCampaign"
import { SummaryDspDeal } from "./SummaryDspDeal"
import { SummaryForecast } from "./SummaryForecast"

interface ISummaryPageProps {
  context: APP_CONTEXTS | null
  isLoading: boolean
  error: IFormError
  inStoreCampaignId: number
  isForecast: boolean
  isDspDeal: boolean
  statusId: CAMPAIGN_STATUSES | null
  contentWidth: number
}

export const SummaryPage = React.memo(
  ({
    context,
    isLoading,
    error,
    inStoreCampaignId,
    isForecast,
    isDspDeal,
    statusId,
    contentWidth
  }: ISummaryPageProps): JSX.Element => {
    const isFirstRenderRef = React.useRef(true)
    const { campaignId: inUrlCampaignId } = useParams()
    React.useEffect(() => {
      if (
        (!isForecast && !isDspDeal && context !== APP_CONTEXTS.CAMPAIGN_EDIT) ||
        (isForecast && context !== APP_CONTEXTS.FORECAST_EDIT) ||
        (isDspDeal && context !== APP_CONTEXTS.DSP_DEAL_EDIT)
      ) {
        setActiveAppContext(
          isForecast ? APP_CONTEXTS.FORECAST_EDIT : isDspDeal ? APP_CONTEXTS.DSP_DEAL_EDIT : APP_CONTEXTS.CAMPAIGN_EDIT
        )
        return
      }
      const toLoadCampaignId = Number(inUrlCampaignId)
      const isLoaded = () => {
        const activeContext = getActiveAppContext()
        return (
          ((!isForecast && !isDspDeal && activeContext === APP_CONTEXTS.CAMPAIGN_EDIT) ||
            (isForecast && activeContext === APP_CONTEXTS.FORECAST_EDIT) ||
            (isDspDeal && activeContext === APP_CONTEXTS.DSP_DEAL_EDIT)) &&
          toLoadCampaignId === inStoreCampaignId &&
          !isFirstRenderRef.current
        )
      }
      const onComponentClose = () => {
        if (isLoaded()) {
          if (closeCurrentSummary()) {
            notifySummaryClosed(form as IForm)
            setActiveAppContext(null)
          }
        }
      }
      if (inUrlCampaignId === "" || inUrlCampaignId === undefined || isLoading) {
        return
      }
      if (/* isClosing || */ isLoaded()) {
        return onComponentClose
      }
      isFirstRenderRef.current = false
      // loadCampaign will change inStoreCampaignId, and so isLoaded will become true
      loadCampaign(toLoadCampaignId, isForecast, isDspDeal).then(newForm => notifySummaryOpened(newForm))
      return onComponentClose
    }, [inStoreCampaignId, context])
    const canSave = canSaveCampaigns()
    const activeContext = getActiveAppContext()
    const form = activeContext ? getForm() : null
    const launchError = form ? translatedLaunchError(form) : ""
    const isTemporary = statusId === CAMPAIGN_STATUSES.TEMPORARY
    return error === "not-found" ||
      inUrlCampaignId === "" ||
      (form &&
        form.campaignId !== NOT_SAVED_ID &&
        form.tempCampaignId !== NOT_SAVED_ID &&
        !isLoading &&
        isTemporary) ? (
      <NotFoundPage
        message={translate(
          isForecast ? "forecast.status.error" : isDspDeal ? "dsp-deal.status.error" : "campaign.status.error"
        )}
      />
    ) : error === "loading" ? (
      <UnexpectedErrorPage
        message={translate(
          isForecast ? "forecast.loading.error" : isDspDeal ? "dsp-deal.loading.error" : "campaign.loading.error"
        )}
      />
    ) : (
      <FlexBox
        id={`${isForecast ? "forecast-summary" : isDspDeal ? "dsp-deal-summary" : "campaign-summary"}`}
        ai="start"
        jc="start"
        vertical
        fullHeight
        sx={{
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          animation: "fadeIn 600ms"
        }}>
        {isLoading && <TestHiddenData id="summary-is-loading" />}
        {!isLoading && <TestHiddenData id="summary-loaded" />}
        <>
          {isLoading ? (
            <LinearLoader
              id="summary-page-is-loading"
              text={translate(
                isForecast
                  ? "forecast.single.loading"
                  : isDspDeal
                    ? "dsp-deal.single.loading"
                    : "campaign.single.loading"
              )}
              sx={{ m: "2rem", width: "calc(100% - 4rem)" }}
            />
          ) : (
            <>
              <PageHeader
                title={translate(`${isForecast ? "forecast" : isDspDeal ? "dsp-deal" : "campaign"}.summary.title`)}
                actionButtons={() => (activeContext && canSave ? getFormActionButtons(getForm(), true) : [])}
              />
              <FlexBox
                vertical
                fullWidth
                fullHeight
                jc="start"
                ai="start"
                sx={{
                  p: "1rem",
                  pt: 0
                }}>
                <FlexBox jc="space-between" wrap gap fullWidth sx={{ mt: "1rem", mb: "1rem" }}>
                  <FlexBox jc="start" ai="start" gap fullWidth>
                    {form && contentWidth > 650 && (
                      <Typography
                        id="summary-campaign-name"
                        overflow={"hidden"}
                        textOverflow={"ellipsis"}
                        whiteSpace={"nowrap"}
                        sx={{
                          fontSize: "1.4rem",
                          fontWeight: 500,
                          mr: "1rem",
                          color: theme => theme.palette.text.primary2
                        }}>
                        {form.configuration.main.name}
                      </Typography>
                    )}
                    {form && !isForecast && (
                      <CampaignStatus id="summary-campaign-status" status={form.statusId} isDspDeal={isDspDeal} />
                    )}
                  </FlexBox>
                  {inStoreCampaignId !== NOT_SAVED_ID &&
                    !isLoading &&
                    form !== null &&
                    activeContext !== null &&
                    isCampaignLaunchableInFormContext(activeContext, form.statusId) &&
                    launchError && (
                      <Typography
                        id="summary-launch-error"
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 500,
                          mr: "1rem",
                          color: theme => theme.palette.error.main
                        }}>
                        {launchError}
                      </Typography>
                    )}
                </FlexBox>
                {!isDspDeal && <FormInventory />}
                {isForecast ? <SummaryForecast /> : isDspDeal ? <SummaryDspDeal /> : <SummaryCampaign />}
              </FlexBox>
            </>
          )}
        </>
      </FlexBox>
    )
  }
)
